import {html, render as renderHTML} from 'lit-html'
import {install, uninstall} from '@github-ui/hotkey'
import type {CheckStepElement} from './check-step'
import {LogParser} from './log-parser'
import type {LogLike} from './log-parser'
import {NodeType} from './parser-types'
import {setCompositeActionsEnabled} from './parser'
import {areCharacterKeyShortcutsEnabled} from '@github-ui/hotkey/keyboard-shortcuts-helper'
import {observe} from '@github-ui/selector-observer'
import {on} from 'delegated-events'
import {repeat} from 'lit-html/directives/repeat'
import {sendCustomMetric, sendStats} from '@github-ui/stats'
import {yieldToMain} from '@github-ui/yield-to-main'
import {buildCompletedLogMetrics, type CompletedLogMetricSample} from './log-viewer/ingestion/completed-log-metrics'
import {
  updateAutoScrollState,
  isAutoScrollEnabled,
  determineScrollDirection,
  getElementToScroll,
  setElementToScroll,
  resetScrollState,
} from './auto-scroll'

export {disableAutoScroll} from './auto-scroll'

const MAX_LOG_SIZE_IN_BYTES = 1024 * 1024 * 10 // 10Mb
// While streaming a completed log, flush to the DOM once this many decoded
// characters have accumulated since the last flush. Bounds the work done before
// the first paint so a large log renders its first screenful quickly instead of
// blocking on a single whole-log parse + cluster build.
const PROGRESSIVE_FLUSH_THRESHOLD = 256 * 1024 // ~256Kb
const LINES_PER_CLUSTER = 50
const ESTIMATED_LINE_LENGTH = 20
// Budget (ms) of synchronous cluster work syncDOM does per turn before yielding
// back to the browser. Kept well under a frame so input handling and paint stay
// responsive, while doing enough work per turn that a large log doesn't stretch
// across thousands of separate tasks (see syncDOM / yieldToMain).
const SYNC_DOM_FRAME_BUDGET_MS = 5
// Set when we programmatically adjust the scroll position to keep the viewport
// anchored while virtualizing clusters, so the scroll listener doesn't mistake
// the adjustment for a user-initiated scroll (which would corrupt the
// auto-scroll / scroll-direction heuristics).
let suppressScrollDirection = false

export const stepsMap: {[externalId: string]: Step} = {}

export function getUnorderedSteps(): Step[] {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  return Object.keys(stepsMap).map(x => stepsMap[x]!)
}

const embeddingContainer = () => document.querySelector('.content-preview-content') as HTMLElement

const scrollableContainer = () =>
  embeddingContainer() || (document.querySelector('.js-full-logs-container') as HTMLElement)
observe('.js-full-logs-container', {
  constructor: HTMLElement,
  add() {
    calculateContainerHeights()
    addScrollListener()
  },
})

const uxRefresh = () => {
  const logsContainer = scrollableContainer()
  const logsContainerHasUxRefreshAttribute = logsContainer?.hasAttribute('data-ux-refresh')
  return logsContainer && logsContainerHasUxRefreshAttribute
}

function scrollListener() {
  if (suppressScrollDirection) {
    // This scroll event was triggered by our own anchor compensation, not the
    // user. Don't update the direction heuristic, just keep the state in sync.
    suppressScrollDirection = false
    resetScrollState()
    determineScrollDirection(calculateScrollTop())
  } else {
    determineScrollDirection(calculateScrollTop())
  }
  scheduleRenderSteps()
}

// Scroll events fire far more often than the browser paints (and faster still on
// trackpads and high-refresh displays), but a virtual-scroll pass can only change
// what the user sees once per frame. `renderSteps` walks every cluster of every
// open step, so running it per event burned whole frames doing work that the next
// event immediately superseded. Coalesce to at most one pass per frame; the
// direction bookkeeping above stays per-event so the `suppressScrollDirection`
// handshake with `adjustScrollBy` is unaffected.
let pendingRenderStepsFrame = 0

function scheduleRenderSteps() {
  if (pendingRenderStepsFrame) return
  pendingRenderStepsFrame = requestAnimationFrame(() => {
    pendingRenderStepsFrame = 0
    renderSteps()
  })
}

function addScrollListener() {
  const container = uxRefresh() ? document : scrollableContainer() || document
  /* eslint-disable-next-line github/prefer-observers */
  container.addEventListener('scroll', scrollListener)
}

function calculateScrollTop(): number {
  // Read from the same active scroll source that renderCluster/adjustScrollBy use:
  // the window only in UX-refresh inline mode, otherwise the log container
  // (fullscreen and the legacy inline viewer both scroll the container). Using
  // window.scrollY for the legacy inline viewer would always read 0 and break the
  // scroll-direction heuristic after anchor compensation.
  return uxRefresh() && !isFullscreen() ? window.scrollY : fullLogsContainer().scrollTop
}

function calculateContainerHeights() {
  if (uxRefresh() && !isFullscreen()) {
    scrollableContainer()?.style.removeProperty('height')
  } else {
    makeScrollable(scrollableContainer())
  }
}

function makeScrollable(element: HTMLElement | null) {
  if (!element) return
  element.style.height = `${window.innerHeight - element.getBoundingClientRect().top}px`
}

calculateContainerHeights()
addScrollListener()

// Log lines wrap (`white-space: pre-wrap`), so a height measured while a cluster was
// on screen is only valid for the width it was measured at. Resizing the window and
// toggling fullscreen both change that width without touching any cluster, which
// would otherwise leave a permanently poisoned measurement behind: nothing re-reads
// it, so the cluster would reserve the wrong placeholder forever. Bumping a counter
// retires every outstanding measurement at once, and each cluster records a fresh one
// the next time it is materialized and torn down.
let measurementGeneration = 0

function invalidateMeasuredHeights() {
  measurementGeneration++
}

/**
 * Retires every measured height, then reconciles the on-screen placeholders.
 *
 * For changes that alter how log lines wrap without changing the container's size or any
 * cluster's renderable-line count. Toggling timestamps is the live example: it is a pure
 * CSS class flip on an ancestor, so it shows or hides a span on every line — changing
 * what each line wraps to — while the scroller keeps its width, no `resize` fires, and
 * `visibleLineCount` is untouched. Neither guard `estimateClusterHeight` relies on can
 * see it, so hidden clusters would go on reserving placeholders sized for the other
 * mode.
 */
export function invalidateRenderedLineHeights() {
  invalidateMeasuredHeights()
  scheduleRenderSteps()
}

function onViewportGeometryChange() {
  calculateContainerHeights()
  invalidateMeasuredHeights()
  // Bumping the generation only retires a measurement; it doesn't resize anything.
  // A hidden cluster keeps its old inline `height` until something calls
  // `estimateClusterHeight` on it, so without a pass here the virtual range stays
  // computed against the pre-resize wrap width until the user next scrolls. The pass
  // is bounded to the viewport, so it reconciles what is on screen and leaves the
  // rest to re-measure lazily when scrolled to.
  scheduleRenderSteps()
}

/* eslint-disable-next-line github/prefer-observers */
window.addEventListener('resize', onViewportGeometryChange)

export const StepState = {
  INITIAL: 0,
  COMPLETED_LOGS_LOADING: 1,
  COMPLETED_LOGS_LOADED: 2,
  STREAMING: 3,
  ERROR: 4,
} as const

export type StepState = (typeof StepState)[keyof typeof StepState]

type Cluster = {
  startIndex: number
  endIndex: number
  wrapper: HTMLElement
  visible: boolean
  height?: number
  // Number of lines in this cluster that are currently renderable (not hidden by
  // a collapsed group/action). Kept in sync by estimateClusterHeight. A value of
  // 0 means the cluster has nothing to show and must never be materialized.
  visibleLineCount?: number
  // Real rendered height, measured while the cluster was on screen, plus the
  // renderable-line count it was measured at. Reserving this instead of the crude
  // ESTIMATED_LINE_LENGTH guess keeps the total document height stable as clusters
  // come and go. That matters beyond scroll anchoring: the run page's layout pane
  // is `position: sticky`, so a document height that changes underneath it makes
  // the pane snap out of place (its background visibly flashes).
  measuredHeight?: number
  measuredLineCount?: number
  // The `measurementGeneration` the height above was taken in. A measurement only
  // describes the cluster while the wrap width it was taken at still holds.
  measuredGeneration?: number
}

type Step = {
  externalId: string
  number: number
  parser: LogParser
  container: HTMLElement
  searchLineIndexes: number[]
  clusters: Cluster[]
  state: StepState
  permalinkLine?: number
  expandedGroups: Set<number>
  /** Tracks which composite action bars are expanded, by their start line index */
  expandedActions: Set<number>
  /** Tracks how many completed action indices were already re-rendered */
  lastCompletedActionCount: number
}

const {href, hash} = window.location
const baseURL = href.substring(0, href.length - hash.length)

const buffer: Record<string, string[]> = {}

export function flushLogs(step: Step) {
  const newLines = buffer[step.externalId]
  if (!newLines) return
  for (const stp of Object.values(stepsMap)) {
    if (step !== stp && !stp.container.hasAttribute('manually-opened')) closeStep(stp)
  }
  updateStreamingLogs(step, newLines, null)
  delete buffer[step.externalId]
}

export function bufferLogs(externalId: string, lines: string[]) {
  buffer[externalId] = (buffer[externalId] || []).concat(lines)
}

export function bufferedStepIds() {
  return Object.keys(buffer)
}

let compositeActionsInitialized = false

export function configureStep(container: HTMLElement, permalinkLine?: number): Step {
  if (!compositeActionsInitialized) {
    compositeActionsInitialized = true
    // eslint-disable-next-line github/get-attribute -- attribute name matches the feature flag name for searchability
    setCompositeActionsEnabled(!!scrollableContainer()?.hasAttribute('data-actions_runner_emit_composite_markers'))
  }
  const number = getStepNumber(container)
  const externalId = container.getAttribute('data-external-id') || ''
  const parser = new LogParser('', `${baseURL}#step:${number}`)

  const step: Step = {
    externalId,
    number,
    parser,
    container,
    searchLineIndexes: [],
    state: StepState.INITIAL,
    clusters: [],
    permalinkLine,
    expandedGroups: new Set<number>(),
    expandedActions: new Set<number>(),
    lastCompletedActionCount: 0,
  }

  moveStep(container)
  stepsMap[externalId] = step
  container.removeEventListener('toggle', handleToggle)
  container.addEventListener('toggle', handleToggle)
  container.removeEventListener('click', handleClick)
  container.addEventListener('click', handleClick)

  flushLogs(step)
  return step
}

function stepFromContainer(container: HTMLElement): Step | null {
  const externalId = container.getAttribute('data-external-id')
  if (!externalId) {
    return null
  }

  return stepsMap[externalId] || null
}

async function handleToggle(event: Event) {
  const container = event.currentTarget as HTMLElement
  const step = stepFromContainer(container)
  if (!step) {
    return
  }

  // This shouldn't be necessary, but we are seeing that heights sometimes are not calculated correctly when the page loads
  calculateContainerHeights()

  if (isOpen(step) && hasInlineDetails(step)) {
    step.state = StepState.COMPLETED_LOGS_LOADED
  } else if (isOpen(step) && step.state !== StepState.COMPLETED_LOGS_LOADED) {
    await loadCompletedLogs(step)
  }
  renderSteps()
}

function handleClick(event: Event) {
  const container = event.currentTarget as HTMLElement
  const step = stepFromContainer(container)
  if (!step) {
    return
  }

  if (!hasInlineDetails(step) && step.state !== StepState.STREAMING && !getCompletedLogURL(step)) {
    event.preventDefault()
    return
  }

  if (!step.container.hasAttribute('open')) {
    step.container.setAttribute('manually-opened', '')
  }
}

function isOpen(step: Step) {
  return step.container.hasAttribute('open')
}

function openStep(step: Step) {
  resetScrollToLogic()
  step.container.setAttribute('open', 'open')
}

export function expandStep(step: Step) {
  openStep(step)
}

function closeStep(step: Step) {
  step.container.removeAttribute('open')
  const element = step.container.closest<CheckStepElement>('check-step')
  if (element) {
    element.removeAttribute('data-in-progress')
    element.removeAttribute('manually-opened')
    element.update()
  }
}

function getCompletedLogURL(step: Step) {
  return step.container.getAttribute('data-log-url')
}

function stepAttribute(step: Step, name: string) {
  return (
    step.container.getAttribute(name) ||
    step.container.closest<CheckStepElement>('check-step')?.getAttribute(name) ||
    ''
  )
}

function hasInlineDetails(step: Step) {
  const stepType = stepAttribute(step, 'data-step-type')
  return stepType === 'wait' || stepType === 'wait-all' || stepType === 'cancel'
}

function isBackgroundStep(step: Step) {
  return step.container.hasAttribute('data-background') || !!step.container.closest('check-step[data-background]')
}

// Order steps in the UI according to their step number
function moveStep(stepContainer: Element) {
  // if descendant of check-step element (new non-postback UI)
  const isCheckStepChild = stepContainer.parentNode?.nodeName === 'CHECK-STEP'

  // if the stepContainer is a child of a check-step, we want to move the check-step parent
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const elementToMove = isCheckStepChild ? stepContainer.parentElement! : stepContainer
  const stepNumber = getStepNumber(elementToMove)

  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const siblingElements = [...elementToMove.parentElement!.children]
  for (const siblingElement of siblingElements) {
    if (siblingElement === elementToMove) break
    const siblingStepNumber = getStepNumber(siblingElement)
    if (+stepNumber < +siblingStepNumber) {
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      elementToMove.parentElement!.insertBefore(elementToMove, siblingElement)
    }
  }
}

function showLoader(step: Step) {
  const {loader, chevron} = queryElements(step)
  loader.removeAttribute('hidden')
  chevron.setAttribute('hidden', 'hidden')
}

function hideLoader(step: Step) {
  const {loader, chevron} = queryElements(step)
  loader.setAttribute('hidden', 'hidden')
  chevron.removeAttribute('hidden')
}

function enableStep(step: Step) {
  const {chevron} = queryElements(step)
  chevron.style.removeProperty('visibility')
  step.container.style.removeProperty('pointer-events')
}

export async function loadCompletedLogs(
  step: Step,
  options?: {keepLoader?: boolean; scrollToBottom?: boolean; permaLink?: boolean},
) {
  options = options || {}

  if (step.state === StepState.COMPLETED_LOGS_LOADING) return
  if (step.state === StepState.COMPLETED_LOGS_LOADED) return

  const url = getCompletedLogURL(step)
  if (!url) return

  resetScrollToLogic()
  if (options.scrollToBottom || options.permaLink) openStep(step)

  const {downloadErrorMessage, downloadErrorMessageText} = queryElements(step)
  downloadErrorMessage.hidden = true
  showLoader(step)
  resetStep(step)
  enableStep(step)

  try {
    step.state = StepState.COMPLETED_LOGS_LOADING

    const response = await fetch(url)

    // if the SSO token expires, we will get an HTML response instead of logs (with an non-error status code)
    // so let's check that the response type is text not html
    const isHTMLContent = response?.headers?.get('Content-Type')?.includes('text/html')

    if (!response.ok || isHTMLContent) {
      let text

      if (response.status === 410) {
        text = 'data-removed-text'
      } else if (response.status === 404) {
        text = 'data-not-found-text'
      } else {
        text = 'data-error-text'
      }

      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      const message = downloadErrorMessageText.getAttribute(text)!

      downloadErrorMessageText.textContent = message
      throw new Error('Response was not OK')
    }

    const reader = response.body && response.body.getReader && response.body.getReader()
    if (reader) {
      await streamCompletedLog(step, reader, options)
    } else {
      const data = await response.text()
      await appendLogs(step, data, options)
    }

    step.state = StepState.COMPLETED_LOGS_LOADED
    initializeActionExpandState(step)
    applyInitialExpandState(step)
    sendStats({incrementKey: 'ACTIONS_COMPLETED_LOG_VIEWS_SUCCEED'})

    if (options.scrollToBottom) {
      scrollToLine(step, step.parser.logLines.length - 1)
    }
  } catch (err) {
    step.state = StepState.ERROR
    downloadErrorMessage.hidden = false
    sendStats({incrementKey: 'ACTIONS_COMPLETED_LOG_VIEWS_FAIL'})
    throw err
  } finally {
    hideLoader(step)
  }
}

export async function updateStreamingLogs(step: Step, newLines: LogLike[], startLine: number | null) {
  if (getCompletedLogURL(step)) return

  if (step.state !== StepState.STREAMING) {
    // change step to in progress while we wait for API update to finish
    // avoids awkward state of logs coming in before status icon has been updated
    const checkStepElement = step.container.closest<CheckStepElement>('check-step')
    if (checkStepElement) {
      checkStepElement.setAttribute('data-in-progress', '')
      // TODO: we can hack started_at here too
      checkStepElement.update()
    }
  }
  step.state = StepState.STREAMING

  if (newLines.length === 0) return

  const firstLines = step.parser.rawLogData.length === 0
  const container = fullLogsContainer()
  const lastElement = findLineElement(step, step.parser.logLines.length - 1)
  const lastElementBottom = lastElement
    ? lastElement.getBoundingClientRect().bottom
    : container.getBoundingClientRect().bottom
  const lastElementInView = isFullscreen()
    ? lastElementBottom < container.getBoundingClientRect().bottom
    : lastElementBottom < fullLogsContainerHeight()

  const shouldScroll = updateAutoScrollState(lastElementInView)

  await appendLogs(step, newLines, {startLine})

  const {loader, logsContainer, downloadErrorMessage, animation} = queryElements(step)
  enableStep(step)

  // Remove the loader and display the logs
  if (step.parser.rawLogData.length > 0 && !loader.getAttribute('hidden')) {
    loader.setAttribute('hidden', 'hidden')
    logsContainer.hidden = false
  }
  // Hide any previous error message
  downloadErrorMessage.hidden = true
  // Improves the UX when we start receiving log lines but the partial
  // for the step summary hasn't loaded yet
  if (animation) animation.removeAttribute('hidden')

  if (firstLines) {
    if (!isBackgroundStep(step)) {
      openStep(step)
      sendStats({incrementKey: 'ACTIONS_STREAMING_LOG_VIEWS_SUCCEED'})
    }
  }

  if (isOpen(step) && shouldScroll) {
    scrollToLine(step, step.parser.logLines.length - 1)
  }
}

async function streamCompletedLog(
  step: Step,
  reader: ReadableStreamDefaultReader<unknown>,
  options?: {keepLoader?: boolean; scrollToBottom?: boolean},
) {
  let bytes = 0
  let truncated = false
  const utf8Decoder = new TextDecoder('utf-8')

  // Instrumentation for progressive ingestion. `startTime` anchors both timings;
  // `firstContentAt` marks when the first batch has been materialized into the DOM,
  // and `flushCount` records how many bounded batches were rendered.
  const startTime = performance.now()
  let firstContentAt = 0
  let flushCount = 0
  // Keep the loader visible between batches: forwarding the default `keepLoader: false`
  // would make a stalled or slow remainder look complete. `loadCompletedLogs` hides the
  // loader in its `finally` once the stream finishes or fails.
  const appendOptions = {...options, keepLoader: true}

  const parts: string[] = []
  let pendingChars = 0

  const appendBatch = async (batch: string) => {
    await appendLogs(step, batch, appendOptions)
    flushCount += 1
    if (firstContentAt === 0) firstContentAt = performance.now()
  }

  const flush = async () => {
    if (parts.length === 0) return
    const batch = parts.join('')
    parts.length = 0
    pendingChars = 0
    await appendBatch(batch)
  }

  for (;;) {
    const chunk = await reader.read()
    if (chunk.done) break
    // chunk.value is always an Uint8Array if done === true
    const value = chunk.value == null ? new Uint8Array(0) : (chunk.value as Uint8Array)

    bytes += value.length

    if (bytes > MAX_LOG_SIZE_IN_BYTES) {
      truncated = true
      break
    }

    // `stream: true` keeps multi-byte UTF-8 sequences intact across chunk
    // boundaries; the trailing flush below emits any buffered remainder.
    const text = utf8Decoder.decode(value, {stream: true})
    parts.push(text)
    pendingChars += text.length

    // appendData() tracks the trailing partial line itself, so batches need not
    // land on line boundaries.
    if (pendingChars >= PROGRESSIVE_FLUSH_THRESHOLD) {
      await flush()
    }
  }
  const tail = utf8Decoder.decode()
  if (tail) parts.push(tail)

  if (truncated) queryElements(step).truncatedMessage.hidden = false

  await flush()

  reportCompletedLogMetrics({
    mode: 'progressive',
    truncated,
    bytes,
    flushCount,
    startTime,
    firstContentAt,
  })
}

// Emits the completed-log rendering metrics to Datadog so the progressive-ingestion
// rollout can be quantified.
function reportCompletedLogMetrics(sample: CompletedLogMetricSample) {
  for (const metric of buildCompletedLogMetrics(sample)) {
    sendCustomMetric(metric)
  }
}

function getStepNumber(stepDetails: Element): number {
  const attr = stepDetails.tagName === 'CHECK-STEP' ? 'data-number' : 'data-step-number'
  const num = stepDetails.getAttribute(attr)
  if (!num) return -1
  return +num
}

function resetStep(step: Step) {
  step.parser.setData('')
  step.clusters = []
  queryElements(step).logsContainer.textContent = ''
}

// We block virtualizing/materializing while scrolling. There are cases where we want to reset this logic.
function resetScrollToLogic() {
  setElementToScroll(null)
}

async function appendLogs(
  step: Step,
  data: string | LogLike[],
  options?: {startLine?: number | null; keepLoader?: boolean; scrollToBottom?: boolean},
) {
  options = options || {}
  const {logsContainer} = queryElements(step)
  const lastCluster = step.clusters[step.clusters.length - 1]
  let startIndex = lastCluster ? lastCluster.startIndex : 0
  // A virtualized cluster that grows during this append needs its visibility
  // re-evaluated. `syncDOM` only walks clusters whose wrapper is not in the DOM
  // yet, so it never revisits this one. See the renderCluster call below.
  let extendedCluster: Cluster | undefined

  const lastLine = step.parser.logLines.length - 1
  if (typeof data === 'string') {
    step.parser.appendData(data)
    // `appendData` can extend the previous trailing partial line in place. That grows the
    // tail cluster's rendered height (the line may now wrap onto more rows) without
    // changing its renderable-line count, so the line-count check in
    // `estimateClusterHeight` cannot catch it and any earlier measurement is stale.
    if (lastCluster) lastCluster.measuredHeight = undefined
  } else {
    step.parser.appendLines(data, options.startLine)
  }
  renderLines(step, [lastLine]) // re-render previous last line, just in case it's changed

  // Re-render action start lines that received conclusion data from end-action markers
  // parsed in this chunk. Without this, action bars show spinners until a manual re-render.
  const newCompletedActions = step.parser.completedActionLineIndices.slice(step.lastCompletedActionCount)
  step.lastCompletedActionCount = step.parser.completedActionLineIndices.length
  if (newCompletedActions.length > 0) {
    renderLines(step, newCompletedActions)
  }

  // Divide the log lines in clusters
  while (startIndex < step.parser.logLines.length) {
    const endIndex = Math.min(startIndex + LINES_PER_CLUSTER, step.parser.logLines.length) - 1

    if (lastCluster && lastCluster.startIndex === startIndex) {
      if (lastCluster.visible) {
        // appendLines needs the previous endIndex to know where the new lines start,
        // so extend the range only after appending.
        appendLines(step, lastCluster, lastCluster.endIndex + 1, endIndex)
        lastCluster.endIndex = endIndex
        // Recount for the new range. `estimateClusterHeight` only touches the inline
        // height of a *hidden* cluster, so for a visible one this is purely the recount
        // — but it is essential: `visibleLineCount` is what a cached measurement is
        // matched against, and leaving it at the pre-append value lets a height measured
        // over the shorter range be reused for the longer one. This is the tail cluster
        // of a streaming log, so it is the hot path, not an edge case.
        //
        estimateClusterHeight(step, lastCluster)
      } else {
        // Extend the range BEFORE estimating, otherwise visibleLineCount/height are
        // computed over the old (shorter) range. If the old range was fully collapsed
        // (visibleLineCount 0) but the newly streamed lines are visible, a stale 0
        // would make renderCluster skip them indefinitely.
        lastCluster.endIndex = endIndex
        estimateClusterHeight(step, lastCluster)
        extendedCluster = lastCluster
      }
    } else {
      const wrapper = document.createElement('div')
      const cluster: Cluster = {
        startIndex,
        endIndex,
        wrapper,
        visible: false,
      }
      estimateClusterHeight(step, cluster)
      step.clusters.push(cluster)
    }

    startIndex += LINES_PER_CLUSTER
  }
  logsContainer.hidden = false
  if (!options.keepLoader) hideLoader(step)
  // Re-run the virtual-scroll decision for a cluster that was extended in place.
  // `syncDOM` keys off `logsContainer.childElementCount`, so it only ever visits
  // clusters whose wrapper has not been appended yet — an already-appended cluster
  // that just gained lines would otherwise stay a blank placeholder until the next
  // scroll event. This is reachable both from progressive completed-log ingestion
  // (every flush boundary lands mid-cluster) and from live streaming, where
  // `updateStreamingLogs` appends repeatedly.
  // Done before syncDOM so the cluster has its real height while the clusters after
  // it measure their own viewport position.
  if (extendedCluster) {
    const view = readViewMetrics()
    renderCluster(step, extendedCluster, view)
    flushScrollAdjustment(view)
  }
  await syncDOM(step, true)
}

// Whether walking this cluster means we have passed the bottom of the viewport, which is
// what tells the materialization loop it can stop computing visibility for the clusters
// after it. A cluster whose lines are all hidden (collapsed group/action) reserves zero
// height and stays `visible: false`, so it is NOT evidence that we reached the bottom —
// treating it as such halts the pass at the first collapsed cluster and leaves every
// cluster after it unmaterialized until the next scroll event.
function isPastViewport(cluster: Cluster): boolean {
  return !cluster.visible && cluster.visibleLineCount !== 0
}

// For a very big log just the fact of adding many empty wrappers at the same time may freeze the browser.
// Materialize clusters in time-budgeted slices, yielding to the browser once
// each slice exhausts SYNC_DOM_FRAME_BUDGET_MS so input and paint aren't starved.
// A time budget avoids the browser's ~4ms-per-task setTimeout clamp, which would
// stretch materializing a large log over many seconds of wall-clock.
async function syncDOM(step: Step, render: boolean) {
  const {logsContainer} = queryElements(step)

  for (;;) {
    // `childElementCount` can exceed `clusters.length` when a concurrent reset (e.g. resetStep on a
    // step transitioning to completed logs) shrinks `step.clusters` while a previously scheduled
    // syncDOM loop is still pending. Bail whenever the DOM has caught up to (or passed) the cluster
    // count to avoid indexing past the end of the array. Re-checked after every yield below.
    if (logsContainer.childElementCount >= step.clusters.length) return

    const deadline = performance.now() + SYNC_DOM_FRAME_BUDGET_MS
    // Layout state can't change while a slice runs synchronously, so read it once per
    // slice rather than once per cluster. Re-read after each yield below, since the
    // browser may have resized or toggled fullscreen in between.
    //
    // Only `renderCluster` consumes the snapshot and `render` only ever goes true ->
    // false, so once it is off nothing in this or any later slice reads it. Skipping
    // the read matters because the previous slice just appended wrappers: building a
    // snapshot flushes that pending layout, which would cost one forced layout per
    // remaining slice of a long log to produce a value no one looks at.
    const view = render ? readViewMetrics() : null
    // Append (and, while still visible, render) clusters until the frame budget is spent. The
    // do/while guarantees forward progress even if the budget is already exhausted on entry.
    do {
      const count = logsContainer.childElementCount
      if (count >= step.clusters.length) break

      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      const cluster = step.clusters[count]!
      logsContainer.append(cluster.wrapper)

      // We just want to render the first clusters (the ones that are visible).
      // As soon as there's some that is not visible, we stop calculating their visibility
      // to speed up the process.
      if (render && view) renderCluster(step, cluster, view)
      render = render && !isPastViewport(cluster)
    } while (performance.now() < deadline)

    // Settle this slice's compensation before yielding: the snapshot is rebuilt after
    // every yield, so anything still owed on it would be dropped.
    if (view) flushScrollAdjustment(view)

    // Draining finished on the last cluster of this slice. Returning here rather than
    // falling through to `yieldToMain` avoids paying an extra scheduler turn just to
    // re-discover we are done.
    if (logsContainer.childElementCount >= step.clusters.length) return

    await yieldToMain()
  }
}

/**
 * Single source of truth for whether a log line is currently renderable, i.e.
 * whether it produces a visible DOM row. Used by BOTH `estimateClusterHeight`
 * (to count reserved height) and `lineTemplate` (to decide whether to render),
 * so a cluster's reserved placeholder height always matches what actually
 * renders. Keeping these in sync avoids reserving height for lines that render
 * as zero-height hidden nodes (e.g. filtered skipped-action bars), which would
 * reintroduce the blank space / scroll jumps this virtualization prevents.
 */
function isLineRenderable(step: Step, line: LogParser['logLines'][number]): boolean {
  // End-action markers carry metadata only and never render.
  if (line.type === NodeType.EndAction) return false

  // Skipped composite steps with no content are filtered out to reduce noise.
  if (line.isActionStart && line.action?.conclusion === 'skipped') {
    const hasContent = line.actionEndLineIndex != null ? line.actionEndLineIndex - line.lineIndex > 1 : false
    if (!hasContent) return false
  }

  // Hidden inside a collapsed group. Group headers don't have .group set on them
  // (only their children do), so they stay visible.
  const groupIndex = line.group?.lineIndex
  if (groupIndex != null && !step.expandedGroups.has(groupIndex)) return false

  // Hidden inside a collapsed action, unless it's the action-start bar itself
  // (those stay visible so the user can click to expand).
  const actionIndex = line.action?.lineIndex
  if (actionIndex != null && !line.isActionStart && !step.expandedActions.has(actionIndex)) return false

  // Hidden when any ancestor action is collapsed (nested actions).
  if (line.parentActionLineIndices) {
    for (const parentIndex of line.parentActionLineIndices) {
      if (!step.expandedActions.has(parentIndex)) return false
    }
  }

  return true
}

/**
 * Estimates the pixel height of an off-screen cluster for the virtual scroll layout.
 * Counts renderable lines (see `isLineRenderable`), then sets the cluster wrapper's
 * height so the scrollbar reflects total content size.
 */
function estimateClusterHeight(step: Step, cluster: Cluster) {
  let i = cluster.startIndex
  let visibleLineCount = 0

  while (i <= cluster.endIndex) {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const line = step.parser.logLines[i++]!
    if (isLineRenderable(step, line)) visibleLineCount++
  }

  // Cache the renderable-line count so the virtual scroller can cheaply skip
  // collapsed clusters (visibleLineCount === 0) without re-scanning lines.
  cluster.visibleLineCount = visibleLineCount

  if (!cluster.visible) {
    // Prefer a real measurement taken while the cluster was on screen. Reverting to
    // the per-line estimate for a cluster whose exact size we already know would
    // resize the placeholder — and with it the document — for no reason.
    const measured =
      cluster.measuredLineCount === visibleLineCount && cluster.measuredGeneration === measurementGeneration
        ? cluster.measuredHeight
        : undefined
    const height = measured ?? visibleLineCount * ESTIMATED_LINE_LENGTH
    cluster.height = height
    cluster.wrapper.style.height = `${height}px`
  }
}

/** Returns the scrollable log container, falling back to the document root. */
function fullLogsContainer() {
  return scrollableContainer() || document.documentElement
}

/**
 * Snapshot of the layout state a whole virtual-scroll pass needs.
 *
 * Every cluster in a pass resolves the same container, mode flags and viewport
 * height, but reading them inside `renderCluster` meant ~6 `document.querySelector`
 * calls plus a redundant `offsetHeight` layout read *per cluster*. A large log has
 * thousands of clusters and `renderSteps` walks all of them on every scroll event,
 * so that redundancy dominated the cost of scrolling. Read once per pass instead.
 *
 * `scrollTop` is the one mutable field: `adjustScrollBy` moves the scroll position
 * mid-pass, so it writes the new value back here rather than making every later
 * cluster re-read it from the DOM.
 */
type ViewMetrics = {
  container: HTMLElement
  uxRefresh: boolean
  fullscreen: boolean
  viewportHeight: number
  scrollTop: number
  // Height compensation owed to the scroller, accumulated across the pass and applied
  // once by `flushScrollAdjustment`. A pass can compensate for several clusters, and
  // moving the scroller once per cluster meant several clamps against a `scrollHeight`
  // that is still changing, plus more than one programmatic move competing for the
  // single `suppressScrollDirection` handshake. Coalescing the pass onto an animation
  // frame made that worse, because one pass now absorbs every scroll event in the frame
  // and so crosses more cluster boundaries at once.
  pendingScrollDelta: number
  // Active (non-collapsed) selection, or null. `setClusterVisibility` must not tear
  // down a cluster the user is selecting across, and it used to answer that by
  // reading `window.getSelection()` + `isCollapsed` per cluster — a forced style
  // read on every cluster of every scroll pass, which profiling attributed several
  // seconds of main-thread time to on large logs. It is invariant for a pass, so it
  // belongs in the snapshot alongside the rest of the per-pass state.
  selection: Selection | null
}

/**
 * The active selection, or null when there is nothing selected. A collapsed
 * selection is just a caret, so it can never span a cluster and is treated as absent.
 */
function getActiveSelection(): Selection | null {
  const selection = window.getSelection()
  return selection && !selection.isCollapsed ? selection : null
}

function readViewMetrics(): ViewMetrics {
  const scrollable = scrollableContainer()
  const refresh = !!uxRefresh()
  const fullscreen = isFullscreen()
  const container = scrollable || document.documentElement
  return {
    container,
    uxRefresh: refresh,
    fullscreen,
    // For UX refresh, use the entire window, unless in full screen mode
    viewportHeight: scrollable && (!refresh || fullscreen) ? scrollable.offsetHeight : window.innerHeight,
    scrollTop: refresh && !fullscreen ? -container.getBoundingClientRect().top : container.scrollTop,
    pendingScrollDelta: 0,
    selection: getActiveSelection(),
  }
}

/** True when the window, rather than the log container, is the active scroll source. */
function windowIsScroller(view: ViewMetrics) {
  return view.uxRefresh && !view.fullscreen
}

/** Returns the visible height of the log viewport, accounting for fullscreen and UX refresh modes. */
function fullLogsContainerHeight() {
  // For UX refresh, use the entire window, unless in full screen mode
  return scrollableContainer() && (!uxRefresh() || isFullscreen())
    ? scrollableContainer().offsetHeight
    : window.innerHeight
}

/**
 * Virtual scroll driver for a single cluster. Checks whether the cluster's
 * wrapper is within the visible viewport and materializes or tears down its
 * DOM content accordingly.
 *
 * Returns true when the cluster starts below the bottom of the viewport, which
 * tells the caller every cluster after it is below too. A collapsed cluster
 * reserves no height and is never evidence of anything, so it returns false.
 */
function renderCluster(step: Step, cluster: Cluster, view: ViewMetrics): boolean {
  // Collapsed regions: a cluster whose lines are all hidden (inside a collapsed
  // group/action) has nothing to show. Never materialize it. Without this, a
  // large collapsed group (e.g. 20k lines) crams hundreds of zero-height clusters
  // into the viewport, materializes them all, and expanding then re-renders every
  // line synchronously — freezing the tab. Skipping also avoids the layout reads
  // below for the (very common) collapsed case.
  if (cluster.visibleLineCount === 0) {
    if (cluster.visible) setClusterVisibility(step, cluster, false, view.selection)
    return false
  }

  const {wrapper, visible} = cluster

  const scrollTop = view.scrollTop
  const wrapperTop = wrapper.offsetTop
  const heightBefore = wrapper.offsetHeight
  const above = scrollTop > wrapperTop + heightBefore
  const below = scrollTop + view.viewportHeight < wrapperTop

  // A cluster located entirely above the viewport anchor: if materializing or
  // virtualizing it changes its height (the estimated placeholder height rarely
  // matches the real rendered height), the visible content below it shifts and
  // the scroll position "jumps". We compensate by the height delta so the user's
  // view stays anchored while scrolling through large composite logs.
  const compensate = wrapperTop < scrollTop

  if (above || below) {
    setClusterVisibility(step, cluster, false, view.selection)
  } else if (!visible) {
    setClusterVisibility(step, cluster, true, view.selection)
  }

  if (compensate) {
    // Reading `offsetHeight` here is the expensive part of the whole pass:
    // `setClusterVisibility` may have just appended lines, so the read forces a
    // synchronous style recalc + layout. Under CPU throttling that shows up as whole
    // frames spent in Layout while scrolling. Skip it only where the delta follows from
    // what *this pass* just did — both shortcuts below reason about the call that just
    // returned, so neither can go stale.
    //
    // A cross-pass measurement cache used to cover a third case: materializing a cluster
    // back to a height recorded for it earlier. That one is deliberately not taken. It
    // is the only shortcut whose input can silently rot, and when it does it asserts a
    // zero delta and skips compensation entirely — producing exactly the content shift
    // the compensation exists to prevent. Paying the read is the cheaper mistake.
    let delta: number
    if (cluster.visible === visible) {
      // Visibility didn't change — including when an active text selection vetoed the
      // teardown — so no geometry changed either. `setClusterVisibility` short-circuits
      // on `visible === cluster.visible` before touching the DOM, so nothing moved.
      delta = 0
    } else if (!cluster.visible) {
      // Torn down: the placeholder reserves `cluster.height`, which
      // `setClusterVisibility` just read off the live element. Pure arithmetic.
      delta = (cluster.height ?? 0) - heightBefore
    } else {
      delta = wrapper.offsetHeight - heightBefore
    }
    if (delta !== 0) adjustScrollBy(delta, view)
  }

  return below
}

/**
 * Shifts the active scroll position by `delta` pixels using the same scroll
 * source `renderCluster` reads from, so height compensation anchors the correct
 * element: the window only in UX-refresh inline mode, otherwise the log
 * container (fullscreen and the legacy inline viewer both scroll the container).
 * Flags the adjustment so the scroll listener treats it as programmatic rather
 * than a user gesture.
 */
function adjustScrollBy(delta: number, view: ViewMetrics) {
  if (delta === 0) return

  // Owe the move rather than making it: `flushScrollAdjustment` settles the whole pass
  // with one write, so the scroller is clamped once against its final `scrollHeight` and
  // exactly one programmatic move is reconciled with `suppressScrollDirection`.
  view.pendingScrollDelta += delta
  view.scrollTop += delta
}

/**
 * Moves the scroller by `delta`, clamped to its range.
 */
function applyScrollAdjustment(delta: number, view: ViewMetrics) {
  const scrollsWindow = windowIsScroller(view)
  const scroller = scrollsWindow ? document.documentElement : view.container

  // Read everything up front so the write below doesn't force a second layout.
  const before = scrollsWindow ? window.scrollY : scroller.scrollTop
  const max = Math.max(scroller.scrollHeight - scroller.clientHeight, 0)
  const target = Math.min(Math.max(before + delta, 0), max)

  if (scrollsWindow) {
    window.scrollTo(0, target)
  } else {
    scroller.scrollTop = target
  }

  // At the top and bottom extremes the scroll position is clamped, so the
  // adjustment is a no-op and no scroll event will fire to consume the suppression.
  // Setting it regardless would swallow the direction update from the user's next
  // real scroll — exactly the situation at the end of a completed log.
  suppressScrollDirection = target !== before
}

/**
 * Settles the compensation a pass accumulated, in one scroll write.
 *
 * Must be called at the end of every pass that can call `adjustScrollBy` — otherwise the
 * pass leaves the viewport un-anchored and the height changes it just made shift the
 * content the user is looking at. `renderCluster` and `renderStep` therefore take an
 * explicit `ViewMetrics` rather than defaulting one, so a caller can't forget to flush
 * the snapshot it created.
 *
 * Flushing spends the snapshot: `view.scrollTop` is left holding the optimistic position
 * from `adjustScrollBy`, which overshoots if the scroller clamped. Nothing reconciles it
 * because no caller reads the snapshot after flushing — `syncDOM` rebuilds it
 * after every yield, and the rest flush as their last act. Don't reuse a flushed view.
 */
function flushScrollAdjustment(view: ViewMetrics) {
  const delta = view.pendingScrollDelta
  if (delta === 0) return
  view.pendingScrollDelta = 0

  applyScrollAdjustment(delta, view)
}

/**
 * Materializes or tears down a cluster's DOM content. When hiding, records the
 * current height and replaces content with an empty placeholder of the same
 * size to preserve scroll position. Preserves visibility if the user has an
 * active text selection within the cluster.
 */
function setClusterVisibility(
  step: Step,
  cluster: Cluster,
  visible: boolean,
  selection: Selection | null = getActiveSelection(),
) {
  const {wrapper} = cluster

  // Consult the selection only when tearing a cluster down — materializing one can
  // never destroy a selection — and before the no-op short-circuit below, so a hidden
  // cluster that a live cross-cluster selection spans is still materialized and its
  // lines stay part of the copied range. `containsNode` walks the DOM, so gating it
  // this way skips it for the overwhelmingly common no-op and materialize cases.
  if (!visible && selection?.containsNode(wrapper, true)) visible = true
  if (visible === cluster.visible) return

  if (visible) {
    appendLines(step, cluster, cluster.startIndex, cluster.endIndex)
    wrapper.style.height = 'auto'
  } else {
    // Reserve an accurate placeholder height so tearing the cluster down doesn't
    // shift the content around it (which makes the scroll position jump/cascade).
    //  - Collapsed clusters (all lines hidden) MUST reserve 0, otherwise a
    //    just-collapsed region leaves blank space below it.
    //  - Content clusters reserve their real rendered height, measured while still
    //    on screen. Log lines wrap (white-space: pre-wrap), so the line-count
    //    estimate is not reliable enough to avoid scroll jumps here.
    if (cluster.visibleLineCount === 0) {
      cluster.height = 0
    } else if (cluster.visible) {
      // Always read the live height here rather than reusing a previous measurement.
      // This value becomes the placeholder the rest of the document is laid out
      // against, so a stale one shifts everything below it — the read is what makes
      // the teardown non-disruptive, and skipping it defeats the purpose.
      cluster.height = wrapper.offsetHeight || cluster.height
      if (cluster.height) {
        cluster.measuredHeight = cluster.height
        cluster.measuredLineCount = cluster.visibleLineCount
        cluster.measuredGeneration = measurementGeneration
      }
    }
    wrapper.style.height = `${cluster.height ?? 0}px`
    wrapper.textContent = ''
  }
  cluster.visible = visible
}

/** Renders a range of log lines into a cluster's DOM using lit-html templates. */
function appendLines(step: Step, cluster: Cluster, startIndex: number, endIndex: number) {
  const lineIndexes = []
  for (let index = startIndex; index <= endIndex; index++) {
    lineIndexes.push(index)
  }
  const template = html`
    ${repeat(lineIndexes, lineIndex => {
      const tmpl = lineTemplate(step, lineIndex)
      if (!tmpl) return html`<div data-index="${lineIndex}" hidden></div>`
      return html`<div data-index="${lineIndex}">${tmpl}</div>`
    })}
  `
  const element = document.createElement('div')
  renderHTML(template, element)
  cluster.wrapper.appendChild(element)
}

/** Runs the virtual scroll pass for a single step, updating all its clusters. */
function renderStep(step: Step, view: ViewMetrics) {
  if (step.state < StepState.COMPLETED_LOGS_LOADED) return

  const {clusters} = step
  for (let i = 0; i < clusters.length; i++) {
    const cluster = clusters[i]
    if (!cluster || !renderCluster(step, cluster, view)) continue

    // This cluster starts below the viewport and clusters are in document order, so
    // every cluster after it is below too. None can become visible, so skip the
    // per-cluster `offsetTop`/`offsetHeight` reads entirely and only reclaim the ones
    // still holding DOM. On a large log that turns thousands of layout reads per
    // frame into a boolean check each.
    for (let j = i + 1; j < clusters.length; j++) {
      const rest = clusters[j]
      if (rest?.visible) setClusterVisibility(step, rest, false, view.selection)
    }
    return
  }
}

/** Top-level scroll handler — renders clusters for all open steps. Pauses while actively scrolling to a target element. */
function renderSteps() {
  const view = readViewMetrics()
  // Don't attempt to re-render steps while we are actively scrolling towards an element. Once the element is within the view
  // we can start processing renders again
  const scrollTarget = getElementToScroll()
  if (scrollTarget) {
    const {container} = view
    const scrollTop = view.uxRefresh ? -container.getBoundingClientRect().top : container.scrollTop
    const elemAbove = scrollTop > scrollTarget.offsetTop + scrollTarget.offsetHeight
    const elemBelow = scrollTop + view.viewportHeight < scrollTarget.offsetTop
    if (!elemAbove && !elemBelow) {
      setElementToScroll(null)
    }
  } else {
    for (const step of getUnorderedSteps()) {
      if (isOpen(step)) renderStep(step, view)
    }
    flushScrollAdjustment(view)
  }
}

/** Queries the step container for key DOM elements (log display, loader, error messages, etc.). */
function queryElements(step: Step) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const logDisplayElement = step.container.querySelector<HTMLElement>('.js-checks-log-display')!
  return {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    logsContainer: logDisplayElement.querySelector<HTMLElement>('.js-checks-log-display-container')!,
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    loader: step.container.querySelector<SVGElement>('.js-check-step-loader')!,
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    downloadErrorMessage: logDisplayElement.querySelector<HTMLElement>('.js-checks-log-display-error')!,
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    downloadErrorMessageText: logDisplayElement.querySelector<HTMLElement>('.js-checks-log-display-error-text')!,
    animation: step.container.querySelector('.js-check-step-icon-animation'),
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    truncatedMessage: logDisplayElement.querySelector<HTMLElement>('.js-checks-log-display-truncated')!,
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    chevron: step.container.querySelector<SVGElement>('.js-check-step-chevron')!,
  }
}

/** Returns true when the log panel is in fullscreen mode (detected by inline position style). */
export function isFullscreen() {
  const checkSteps = document.querySelector<HTMLElement>('.js-check-steps')
  return !!checkSteps?.style?.position
}

/** Highlights matching lines in a step and optionally scrolls to the first match. */
export function searchStep(step: Step, text: string, selectFirstLine: boolean) {
  cancelSearchStep(step)

  const searchLineIndexes = step.parser.search(text, selectFirstLine)
  step.searchLineIndexes = searchLineIndexes
  if (selectFirstLine && searchLineIndexes.length > 0) {
    renderLines(step, step.searchLineIndexes)
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    scrollToLine(step, searchLineIndexes[0]!, {select: true})
  }
  return searchLineIndexes
}

/** Clears search highlights and re-renders previously highlighted lines. */
export function cancelSearchStep(step: Step) {
  step.parser.cancelSearch()
  renderLines(step, step.searchLineIndexes)
  step.searchLineIndexes = []
}

/** Re-renders a single line in-place, or hides it if the template returns null. */
function renderLine(step: Step, index: number, element: HTMLElement) {
  const template = lineTemplate(step, index)
  if (template === null) {
    element.hidden = true
    return
  }
  element.hidden = false
  renderHTML(template, element)
}

/**
 * Returns the rendered HTML for a single log line, or null if the line should
 * be hidden (e.g. inside a collapsed group/action, an end-action marker, or a
 * filtered skipped-action bar). Uses the same `isLineRenderable` predicate as
 * `estimateClusterHeight` so reserved height matches what actually renders.
 */
function lineTemplate(step: Step, index: number) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const line = step.parser.logLines[index]!

  if (!isLineRenderable(step, line)) return null

  const groupExpanded = line.isGroup && step.expandedGroups.has(index)
  const actionExpanded = line.isActionStart && step.expandedActions.has(index)
  return step.parser.renderTemplate(line, !!groupExpanded, false, !!actionExpanded)
}

/**
 * Retires the cached height measurement of every cluster owning one of these lines.
 *
 * `renderLines` swaps a line's rendered content in place, which changes the cluster's
 * height without changing its renderable-line count — so the guard `estimateClusterHeight`
 * relies on cannot catch it, and a hidden cluster would keep reserving a placeholder
 * sized for content it no longer holds. That is reachable on every streaming chunk:
 * `appendLogs` re-renders the previous tail line and every action bar that just gained a
 * conclusion, and those indices can land in clusters the user has already scrolled past.
 * Search re-highlighting reaches it on completed logs too.
 *
 * Clusters are cut at fixed `LINES_PER_CLUSTER` boundaries, so the owning cluster is an
 * index rather than a search. The measurement is retired even when the line currently
 * has no element: a virtualized cluster whose template output changed will render at a
 * different height the next time it is materialized.
 */
function invalidateLineMeasurements(step: Step, lineIndexes: number[]) {
  for (const index of lineIndexes) {
    const cluster = step.clusters[Math.floor(index / LINES_PER_CLUSTER)]
    if (cluster && index >= cluster.startIndex && index <= cluster.endIndex) cluster.measuredHeight = undefined
  }
}

/** Re-renders a batch of lines by their indexes, looking up each element in the DOM. */
function renderLines(step: Step, lineIndexes: number[]) {
  invalidateLineMeasurements(step, lineIndexes)
  for (const index of lineIndexes) {
    const element = findLineElement(step, index)
    if (element) renderLine(step, index, element)
  }
}

/** Finds a line's DOM element by its data-index attribute. */
function findLineElement(step: Step, lineIndex: number) {
  return step.container.querySelector(`[data-index='${lineIndex}']`) as HTMLElement
}

/**
 * Scrolls a specific log line into view. Materializes surrounding clusters first
 * to prevent layout shifts, expands any collapsed group/action containing the line,
 * and optionally highlights it as the selected permalink line.
 */
export function scrollToLine(step: Step, lineIndex: number, options?: {select?: boolean}) {
  const {select} = options || {}
  if (lineIndex < 0 || step.clusters.length === 0) return

  openStep(step)

  // If the target sits inside a collapsed group/action, expand it up front (and
  // refresh the cached visibleLineCount/heights) so its cluster is no longer
  // treated as collapsed when we materialize below. Doing this first lets the
  // materialization pass skip genuinely-collapsed clusters without dropping the
  // target.
  const targetLine = step.parser.logLines[lineIndex]
  const targetGroupIndex = targetLine?.group?.lineIndex
  const targetActionIndex = targetLine?.action?.lineIndex
  const needsExpand =
    (targetGroupIndex != null && !step.expandedGroups.has(targetGroupIndex)) ||
    (targetActionIndex != null && !targetLine?.isActionStart && !step.expandedActions.has(targetActionIndex)) ||
    !!targetLine?.parentActionLineIndices?.some(parentIndex => !step.expandedActions.has(parentIndex))
  if (needsExpand) {
    expandGroup(step, lineIndex)
    expandAction(step, lineIndex)
    for (const cluster of step.clusters) estimateClusterHeight(step, cluster)
  }

  // Before scrolling ensure that we have materialized at least +/- 50 lines on either side
  // This ensures we do not materialize another cluster once we scroll to the index
  // which would change the visible elements. Collapsed clusters (visibleLineCount 0)
  // are skipped — they have nothing to show and would only recreate hidden DOM nodes.
  const skipVirtualization = step.clusters.length < 100
  for (const cluster of step.clusters) {
    if (cluster.visibleLineCount === 0) continue
    if (
      skipVirtualization ||
      (lineIndex - LINES_PER_CLUSTER <= cluster.endIndex && lineIndex + LINES_PER_CLUSTER >= cluster.startIndex)
    ) {
      setClusterVisibility(step, cluster, true)
    }
  }

  // Additionally render neighboring clusters in case we step into a small cluster in betwen two other open clusters.
  const orderedSteps = getUnorderedSteps().sort((a, b) => a.number - b.number)
  const stepIndex = orderedSteps.findIndex(x => x.number === step.number)
  ensureEnoughMaterializedLines(orderedSteps, stepIndex, LINES_PER_CLUSTER - (lineIndex + 1), true)
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const linesAwayFromEnd = LINES_PER_CLUSTER - (step.clusters[step.clusters.length - 1]!.endIndex - lineIndex)
  ensureEnoughMaterializedLines(orderedSteps, stepIndex, linesAwayFromEnd, false)

  const element = findLineElement(step, lineIndex)
  if (!element) return

  setElementToScroll(element)
  // If we expanded a collapsed region above, re-render the (now visible) target line.
  if (needsExpand && select) renderLine(step, lineIndex, element)
  if (select) selectLine(element)

  // Ideally we don't do this but we only add expanded groups after the grouptoggle handler is triggered.
  setTimeout(async () => {
    // Re-check: if auto-scroll was disabled while this was queued (e.g. CI
    // Analysis partial mounted between scheduling and execution), abort unless
    // this is a user-initiated scroll (select = permalink navigation).
    if (!select && !isAutoScrollEnabled()) {
      setElementToScroll(null)
      return
    }
    getElementToScroll()?.scrollIntoView({behavior: skipVirtualization ? 'smooth' : 'auto', block: 'center'})
  }, 0)
}

/**
 * Materializes clusters in neighboring steps above or below the current step,
 * so the scroll target has enough rendered content around it to avoid jumps.
 */
function ensureEnoughMaterializedLines(
  orderedSteps: Step[],
  currentStep: number,
  linesToMaterialize: number,
  above: boolean,
) {
  let linesLeft = linesToMaterialize

  const increment = above ? -1 : 1
  let i = currentStep + increment
  while (linesLeft > 0 && ((above && i >= 0) || (!above && i < orderedSteps.length))) {
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const neighborStep = orderedSteps[i]!
    if (neighborStep.clusters?.length > 0 && isOpen(neighborStep)) {
      // Scan inward from the edge nearest the target through this step's clusters,
      // materializing visible ones until the buffer is filled. If we only checked
      // the edge cluster and it were collapsed, we'd skip the whole step's buffer
      // even when inner clusters have visible rows — leaving a gap that causes a
      // late materialization and scroll jump. Decrement by visibleLineCount (the
      // rows actually rendered) and skip collapsed clusters.
      const clusters = neighborStep.clusters
      const dir = above ? -1 : 1
      for (let c = above ? clusters.length - 1 : 0; linesLeft > 0 && c >= 0 && c < clusters.length; c += dir) {
        /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
        const cluster = clusters[c]!
        const visibleLines = cluster.visibleLineCount ?? cluster.endIndex - cluster.startIndex + 1
        if (visibleLines === 0) continue
        setClusterVisibility(neighborStep, cluster, true)
        linesLeft -= visibleLines
      }
    }
    i += increment
  }
}

/** Expands the group containing the given line so it becomes visible. */
function expandGroup(step: Step, lineIndex: number) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const line = step.parser.logLines[lineIndex]!
  const groupIndex = line.group?.lineIndex
  if (groupIndex == null) return
  step.expandedGroups.add(groupIndex)
  const details = step.container.querySelector(`[data-index='${groupIndex}'] details`)
  if (details) details.setAttribute('open', 'open')
}

/** Marks a line element as the selected permalink target, clearing any previous selection. */
function selectLine(element: HTMLElement) {
  for (const prev of document.querySelectorAll('.js-check-step-line.selected')) {
    prev.classList.remove('selected')
  }
  // Intentionally hidden lines (e.g. filtered skipped-action markers) have no
  // .js-check-step-line child; tolerate that instead of dereferencing null.
  element.querySelector<HTMLElement>('.js-check-step-line')?.classList.add('selected')
}

/** Finds the Step object that owns the given child DOM element. */
export function findStep(childElement: Element) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const container = childElement.closest<HTMLElement>('.js-checks-log-details')!
  return getUnorderedSteps().find(step => step.container === container)
}

/** Toggles a group's expand/collapse state and re-renders affected clusters. */
export function toggleGroup(step: Step, index: number, open: boolean) {
  if (open) {
    step.expandedGroups.add(index)
  } else {
    step.expandedGroups.delete(index)
  }

  const lines = step.parser.logLines.filter(line => line.group?.lineIndex === index)
  if (lines.length === 0) return

  const lineIndexes = lines.map(line => line.lineIndex)
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const minLine = lineIndexes[0]!
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const maxLine = lineIndexes[lineIndexes.length - 1]!

  // Write-only pass: update off-screen cluster height estimates and re-render
  // the lines of clusters that are already on screen. We deliberately do NOT
  // call renderCluster() here — reading layout (offsetTop/offsetHeight) right
  // after writing style.height on each cluster forces a synchronous reflow per
  // iteration, which is O(n) reflows for large actions. The single renderSteps()
  // below handles all virtualization in one batched read pass instead.
  for (const cluster of step.clusters) {
    if (cluster.startIndex <= maxLine && cluster.endIndex >= minLine) {
      estimateClusterHeight(step, cluster)
      if (cluster.visible) {
        const clusterLineIndexes = lineIndexes.filter(idx => idx >= cluster.startIndex && idx <= cluster.endIndex)
        renderLines(step, clusterLineIndexes)
      }
    }
  }

  // clusters after the group may have change visibility
  renderSteps()
}

/** Toggles an action bar's expand/collapse state and re-renders affected clusters. */
export function toggleAction(step: Step, index: number, open: boolean) {
  if (open) {
    step.expandedActions.add(index)
  } else {
    step.expandedActions.delete(index)
  }

  const startLine = step.parser.logLines[index]
  if (!startLine) return

  const endLineIndex = startLine.actionEndLineIndex ?? step.parser.logLines.length - 1

  // Write-only pass: update off-screen cluster height estimates and re-render
  // the lines of clusters that are already on screen. We deliberately do NOT
  // call renderCluster() here — reading layout (offsetTop/offsetHeight) right
  // after writing style.height on each cluster forces a synchronous reflow per
  // iteration, which is O(n) reflows for large actions. The single renderSteps()
  // below handles all virtualization in one batched read pass instead.
  for (const cluster of step.clusters) {
    if (cluster.startIndex <= endLineIndex && cluster.endIndex >= index) {
      estimateClusterHeight(step, cluster)
      if (cluster.visible) {
        const from = Math.max(index, cluster.startIndex)
        const to = Math.min(endLineIndex, cluster.endIndex)
        const clusterLineIndexes: number[] = []
        for (let i = from; i <= to; i++) clusterLineIndexes.push(i)
        renderLines(step, clusterLineIndexes)
      }
    }
  }

  renderSteps()
}

/**
 * Sets the initial expand/collapse state for all action bars in a step.
 * Failed and in-progress (no end marker yet) actions are auto-expanded;
 * everything else starts collapsed.
 */
export function initializeActionExpandState(step: Step) {
  step.expandedActions.clear()
  for (const line of step.parser.logLines) {
    if (!line.isActionStart || !line.action) continue
    const action = line.action
    // Auto-expand failed or in-progress actions
    if (action.conclusion === 'failure' || action.endLineIndex == null) {
      step.expandedActions.add(line.lineIndex)
    }
  }
}

/**
 * Applies the initial `expandedActions` set to the DOM in a single batched pass.
 *
 * This replaces looping `toggleAction(step, index, true)` over every auto-expanded
 * action on load, which was O(expandedActions x clusters) AND ran a full
 * `renderSteps()` per action. The cluster heights produced during `appendLogs`
 * assumed the collapsed state, so here we re-estimate every cluster once, re-render
 * the lines of clusters that are already on screen (their DOM still reflects the
 * collapsed state), and run a single virtualization pass.
 */
function applyInitialExpandState(step: Step) {
  if (step.expandedActions.size === 0) return

  for (const cluster of step.clusters) {
    estimateClusterHeight(step, cluster)
    if (cluster.visible) {
      const clusterLineIndexes: number[] = []
      for (let i = cluster.startIndex; i <= cluster.endIndex; i++) clusterLineIndexes.push(i)
      renderLines(step, clusterLineIndexes)
    }
  }

  renderSteps()
}

/** Expands the action (and any parent actions) containing the given line so it becomes visible. */
function expandAction(step: Step, lineIndex: number) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const line = step.parser.logLines[lineIndex]!
  const actionIndex = line.action?.lineIndex
  if (actionIndex != null) {
    step.expandedActions.add(actionIndex)
    const details = step.container.querySelector(`[data-index='${actionIndex}'] .js-checks-action-region`)
    if (details) details.setAttribute('open', 'open')
  }
  // Also expand parent actions
  if (line.parentActionLineIndices) {
    for (const parentIndex of line.parentActionLineIndices) {
      step.expandedActions.add(parentIndex)
      const details = step.container.querySelector(`[data-index='${parentIndex}'] .js-checks-action-region`)
      if (details) details.setAttribute('open', 'open')
    }
  }
}

/** Scrolls to and optionally selects a line by its 1-based permalink number. */
export function selectPermalinkLine(step: Step, permalinkLine: number, scroll: boolean) {
  const lineIndex = step.parser.indexFromNumber(permalinkLine)
  if (lineIndex < 0) return

  if (scroll) {
    scrollToLine(step, lineIndex, {select: true})
  } else {
    const element = findLineElement(step, lineIndex)
    if (element) selectLine(element)
  }
}

/** Toggles the log panel between fullscreen and inline mode, adjusting scroll listeners and sticky headers. */
function toggleFullScreen() {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const checkSteps = document.querySelector<HTMLElement>('.js-check-steps')!

  setFullScreenKeyboardShortcuts()
  resetScrollState()
  // Fullscreen is a class/style toggle, so it fires no `resize` event even though it
  // changes the width log lines wrap at. Retire measured heights explicitly.
  invalidateMeasuredHeights()

  if (isFullscreen()) {
    // Turn the logs container back into what it was before becoming full screen
    if (uxRefresh()) {
      const container = fullLogsContainer()
      const stepHeaders = container.querySelectorAll<HTMLElement>('.js-check-step-summary')
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      const logToolbar = document.querySelector<HTMLElement>('.js-checks-log-toolbar')!

      scrollableContainer()?.removeEventListener('scroll', scrollListener)
      /* eslint-disable-next-line github/prefer-observers */
      document.addEventListener('scroll', scrollListener)

      container.classList.remove('overflow-y-scroll')
      logToolbar.classList.add('uxr_CheckRun-header')

      for (const stepHeader of stepHeaders) {
        stepHeader.classList.add('uxr_CheckStep-header')
        stepHeader.classList.remove('top-0')
        stepHeader.style.removeProperty('sticky')
      }
    }

    for (const prop of ['position', 'top', 'left', 'right', 'bottom', 'zIndex']) {
      checkSteps.style.removeProperty(prop)
    }
    checkSteps.classList.add('rounded-md-1')
  } else {
    // Turn the logs container into a full screen scrollable element
    if (uxRefresh()) {
      const container = fullLogsContainer()
      const stepHeaders = container.querySelectorAll<HTMLElement>('.js-check-step-summary')
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      const logToolbar = document.querySelector<HTMLElement>('.js-checks-log-toolbar')!

      /* eslint-disable-next-line github/prefer-observers */
      scrollableContainer()?.addEventListener('scroll', scrollListener)
      document.removeEventListener('scroll', scrollListener)

      container.classList.add('overflow-y-scroll')
      logToolbar.classList.remove('uxr_CheckRun-header')

      for (const stepHeader of stepHeaders) {
        stepHeader.classList.remove('uxr_CheckStep-header')
        stepHeader.classList.add('top-0')
        stepHeader.style.position = 'sticky'
      }
    }

    checkSteps.style.position = 'fixed'
    checkSteps.style.left = '0'
    checkSteps.style.right = '0'
    checkSteps.style.top = '0'
    checkSteps.style.bottom = '0'
    checkSteps.style.zIndex = '1000'
    checkSteps.classList.remove('rounded-md-1')
  }
  calculateContainerHeights()
  renderSteps()
}

/** Updates hotkey bindings for fullscreen toggle — Escape only available when already fullscreen. */
function setFullScreenKeyboardShortcuts() {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const fullScreenToggle = document.querySelector<HTMLElement>('.js-checks-log-fullscreen')!
  uninstall(fullScreenToggle)

  if (areCharacterKeyShortcutsEnabled()) {
    fullScreenToggle.setAttribute('data-hotkey', isFullscreen() ? 'Shift+F' : 'Shift+F,Escape')
  } else {
    fullScreenToggle.setAttribute('data-hotkey', isFullscreen() ? '' : 'Escape')
  }
  /* eslint-disable-next-line github/no-dataset */
  install(fullScreenToggle, fullScreenToggle.dataset.shortcut)
}

on('change', '.js-checks-log-fullscreen', toggleFullScreen)
